import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Button,
  Typography,
  Grid
} from "@mui/material";
import {
  Add as AddIcon,
} from '@mui/icons-material';
import { usePortalTranslation, TranslationEnum } from "../../providers/TranslationProvider";
import "./LoyaltyManagement.css";
import { Loader } from "../../components/loader/Loader";
import { Task, getLoyaltyStateFromLocalStorage, updateLoyaltyStateInLocalStorage, removeLoyaltyStateFromLocalStorage } from './utils';
import { normalizeText } from '../../utils/utils';
import {
  setTasks,
  setSelectedModule
} from "../../stateManagement/actions/loyaltyManagementActions";
import TaskStatus from "./TaskStatus";
import TaskDetails from "./TaskDetails";
import { getClientConfiguartions } from '../../services/updateTransaltionService';
import { getTasks } from "../../services/loyaltyService";
import { openPopup } from "../../stateManagement/actions/popupActions";
import ComingSoon from "../../components/comingSoon/ComingSoon";
import { logAnalyticsEvent, logLoadTrace } from "../../../src/firebase";

const LoyaltyManagement: React.FC = () => {
  const { translate } = usePortalTranslation();
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(0);
  const [configData, setConfigData] = useState<any>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { tasks } = useSelector((state: any) => state.loyaltyManagement);

  const [activeModules, setActiveModules] = useState<any[]>([]);

  useEffect(() => {
    const savedState = getLoyaltyStateFromLocalStorage();
    if (savedState?.selectedModule) {
      dispatch(setSelectedModule(savedState.selectedModule));
    }
  }, []);

  const getTaskCounts = useCallback(() => {
    const counts: Record<string, number> = {};
    activeModules.forEach(module => {
      counts[module.taskName] = tasks.filter(
        (task: Task) => normalizeText(task.taskName) === normalizeText(module.taskName)
      ).length;
    });
    return counts;
  }, [activeModules, tasks]);

  useEffect(() => {
    const loadTasksAndModules = async () => {
      setShowLoader(true);

      const requestBody = {
        outletcode: null,
        channel: null,
        distributor: null,
        outletClass: null,
      };

      try {
        const [modulesResult, tasksResult] = await Promise.allSettled([
          getClientConfiguartions("LoyaltyConfig"),
          getTasks(requestBody),
        ]);

        if (modulesResult.status === "fulfilled") {
          const allConfigs = modulesResult.value?.data?.features ?? [];
          const domainValues = allConfigs?.[0]?.domainValues ?? [];
          setConfigData(domainValues);
          const loyaltyTaskInfo = domainValues.find(
            (config: any) => config.name === "LoyaltyTaskInfo"
          );

          if (loyaltyTaskInfo?.value?.length) {
            const modulesData = loyaltyTaskInfo.value;
            setActiveModules(modulesData);

            const savedState = getLoyaltyStateFromLocalStorage();
            if (!savedState?.selectedModule) {
              const firstModule = modulesData[0].taskName;
              dispatch(setSelectedModule(firstModule));
              updateLoyaltyStateInLocalStorage({ selectedModule: firstModule });
            }
          }
        } else {
          openPopup('Error', 'Failed to fetch metadata');
          console.error("Error fetching module metadata:", modulesResult.reason);
        }

        if (tasksResult.status === "fulfilled") {
          const tasksData = tasksResult.value;
          dispatch(setTasks(tasksData));
        } else {
          openPopup('Error', 'Failed to fetch tasks');
          console.error("Error fetching tasks:", tasksResult.reason);
        }
      } catch (error) {
        console.error("Unexpected error:", error);
      } finally {
        setShowLoader(false);
      }
    };

    logLoadTrace(loadTasksAndModules, "loyalty_tasks_page_load_time");
  }, [dispatch]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleModuleClick = (moduleName: string) => {
    dispatch(setSelectedModule(moduleName));
    updateLoyaltyStateInLocalStorage({ selectedModule: moduleName });
  };

  const handleCreateTask = () => {
    logAnalyticsEvent("create_new_task_click", {
      total_existing_tasks: tasks.length
    });
    navigate("create", { state: { activeModules, configData, isEditMode: false } });
  };

  const handleModuleUpdate = (updatedModules: any[]) => {
    setActiveModules(updatedModules);
  };

  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes('loyaltyManagement')) {
        removeLoyaltyStateFromLocalStorage();
        dispatch(setSelectedModule(""));
      }
    };
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={4.25}>
              <TaskStatus
                activeModules={activeModules}
                configData={configData}
                taskCounts={getTaskCounts()}
                onModuleClick={handleModuleClick}
                onModulesUpdate={handleModuleUpdate}
              />
            </Grid>
            <Grid item xs={12} md={7.75}>
              <TaskDetails activeModules={activeModules} configData={configData} />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
            flexDirection: 'column',
            gap: 2
          }}>
            <ComingSoon />
          </Box>
        );

      case 2:
        return (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
            flexDirection: 'column',
            gap: 2
          }}>
            <ComingSoon />
          </Box>
        );

      default:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <TaskStatus
                activeModules={activeModules}
                configData={configData}
                taskCounts={getTaskCounts()}
                onModuleClick={handleModuleClick}
                onModulesUpdate={handleModuleUpdate}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <TaskDetails activeModules={activeModules} configData={configData} />
            </Grid>
          </Grid>
        );
    }
  };

  return (
    <div className="loyaltyManagement-wrapper">
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mt: 1,
        mb: 0,
        width: "100%"
      }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: "4px",
              height: "20px",
              backgroundColor: "var(--dashboard-theme-color)",
              borderRadius: "9999px",
              mr: 1
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: "1.1rem",
              color: "#344257"
            }}
          >
            {translate(TranslationEnum.loyalty_management, "Loyalty Management")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            onClick={() => navigate(-1)}
            sx={{
              padding: '4px 12px',
              border: 'none',
              borderRadius: '3px',
              color: 'white',
              fontWeight: 700,
              width: '70px',
              height: '32px',
              backgroundColor: '#808080',
              boxShadow: 'none',
              textTransform: 'none',
              fontSize: '0.75rem',
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: '#808080',
                boxShadow: 'none',
              },
              '&:active': {
                backgroundColor: '#707070',
              }
            }}
          >
            Back
          </Button>
        </Box>
      </Box>
      {showLoader ? (
        <div className="loader-center">
          <Loader />
        </div>
      ) : (
        <>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 0.5,
            borderRadius: 1,
          }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                backgroundColor: '#E7ECEF',
                borderRadius: 1,
                height: 36,
                minHeight: 36,
                padding: '2px',
                '& .MuiTabs-flexContainer': {
                  gap: '4px'
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  color: '#627984',
                  fontSize: '13px',
                  minWidth: 90,
                  minHeight: 32,
                  height: 32,
                  borderRadius: 1,
                  padding: '4px 8px',
                  border: '1px solid transparent',
                  backgroundColor: 'transparent',
                  '&.Mui-selected': {
                    backgroundColor: '#fff',
                    color: '#000',
                    fontWeight: 600,
                    border: '1px solid #D0D5DD'
                  },
                },
                '& .MuiTabs-indicator': {
                  display: 'none'
                }
              }}
            >
              <Tab label="Manage Tasks" disableRipple />
              <Tab label="Dashboard" disableRipple />
              <Tab label="Reward Settings" disableRipple />
            </Tabs>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateTask}
              sx={{
                bgcolor: '#3BCDB7',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#57d6c3',
                  boxShadow: 'none'
                },
                textTransform: 'none',
                borderRadius: 1,
                px: 1,
                py: 0.5,
                fontWeight: 700,
                fontSize: '13px',
                height: 35,
                minHeight: 35,
                '& .MuiButton-startIcon': {
                  marginRight: '4px',
                },
              }}
            >
              Create New Task
            </Button>
          </Box>

          {renderTabContent()}
        </>
      )}
    </div>
  );
};

export default LoyaltyManagement;